import {
  isNovaPoshtaBranchDelivery,
  isNovaPoshtaCourierDelivery,
  isNovaPoshtaLockerDelivery,
} from "../api/checkoutDeliverySettingsApi";

export const NOVA_POST_API_VERSION = "v.1.0";
export const NOVA_POST_PROVIDER = "nova-poshta";

const stripCityTypePrefix = (value) =>
  String(value || "")
    .trim()
    .replace(/^(?:місто|м\.)\s*/iu, "")
    .trim();

export const formatNovaPostCityLabel = (city = {}, fallback = "") => {
  const source = city?.name || fallback;
  const parts = String(source || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  const name = stripCityTypePrefix(parts.shift() || "");
  const region = String(city?.areaName || city?.regionName || parts[0] || "").trim();
  if (!name) return region;
  if (!region || name.toLocaleLowerCase("uk-UA").includes(region.toLocaleLowerCase("uk-UA"))) {
    return name;
  }
  return `${name}, ${region}`;
};

export const formatNovaPostDivisionLabel = (division = {}, fallback = "") => {
  const source = String(division?.name || fallback || "").trim();
  const withoutDuplicatedAddress = source.split(/\s+—\s+/u)[0].trim();
  const isParcelLocker = String(division?.category || "").trim() === "Postomat";
  let label = withoutDuplicatedAddress;
  if (!isParcelLocker) {
    label = label
      .replace(/^відділення(?:\s+"Нової пошти")?\s*/iu, "")
      .trim();
  }
  return label.replace(/№\s*(\d+)/gu, "№ $1");
};

export const getNovaPoshtaDeliveryMethod = (deliveryMethod) => {
  if (isNovaPoshtaLockerDelivery(deliveryMethod)) return "parcelLocker";
  if (isNovaPoshtaBranchDelivery(deliveryMethod)) return "branch";
  if (isNovaPoshtaCourierDelivery(deliveryMethod)) return "courier";
  return "";
};

export const createEmptyNovaPoshtaDelivery = (method = "") => ({
  apiVersion: NOVA_POST_API_VERSION,
  provider: NOVA_POST_PROVIDER,
  method,
  serviceType: "",
  city: { id: null, ref: "", name: "", regionName: "", areaName: "" },
  ...(method === "courier"
    ? {
        courierAddress: {
          fullAddress: "",
          streetId: "",
          streetRef: "",
          streetName: "",
          building: "",
          apartment: "",
          entrance: "",
          floor: "",
          postCode: "",
          note: "",
        },
      }
    : {
        division: {
          id: null,
          divisionId: null,
          ref: "",
          number: "",
          divisionNumber: "",
          name: "",
          address: "",
          category: "",
          status: "",
          prohibitedIssuance: false,
          settlementId: null,
        },
      }),
});

export const settlementFromSelection = (selection = {}) => {
  const settlement = selection.settlement || selection;
  return {
    id: Number(settlement.id) || null,
    ref: String(settlement.ref || ""),
    name: String(settlement.name || selection.displayName || ""),
    regionName: String(settlement.regionName || ""),
    areaName: String(settlement.areaName || ""),
  };
};

export const divisionFromSelection = (selection = {}) => {
  const division = selection.division || selection;
  return {
    id: Number(division.id || division.divisionId) || null,
    divisionId: Number(division.divisionId || division.id) || null,
    ref: String(division.ref || selection.warehouseRef || ""),
    number: String(division.number || division.divisionNumber || ""),
    divisionNumber: String(division.divisionNumber || division.number || ""),
    name: String(division.name || selection.displayName || ""),
    address: String(division.address || selection.fullAddress || ""),
    category: String(division.category || selection.divisionCategory || ""),
    status: String(division.status || selection.status || ""),
    prohibitedIssuance: Boolean(
      division.prohibitedIssuance || selection.prohibitedIssuance
    ),
    settlementId:
      Number(division.settlementId || selection.settlement?.id) || null,
  };
};

const joinDivisionLabel = (division = {}) => {
  const name = String(division.name || "").trim();
  const address = String(division.address || "").trim();
  if (!name) return address;
  if (!address || name.toLocaleLowerCase("uk-UA").includes(address.toLocaleLowerCase("uk-UA"))) {
    return name;
  }
  return `${name} — ${address}`;
};

export const buildSavedNovaPoshtaAddressPayload = ({
  citySelection,
  divisionSelection,
  method = "branch",
} = {}) => {
  const city = settlementFromSelection(citySelection);
  const division = divisionFromSelection(divisionSelection);
  return {
    city: formatNovaPostCityLabel(city),
    branchNumber: formatNovaPostDivisionLabel(
      division,
      joinDivisionLabel(division)
    ),
    novaPoshtaDelivery: {
      apiVersion: NOVA_POST_API_VERSION,
      provider: NOVA_POST_PROVIDER,
      method,
      serviceType: "",
      city,
      division,
    },
  };
};

export const buildCourierAddress = (source = {}) => {
  const streetName = String(source.streetName || source.street || "").trim();
  const building = String(source.building || source.buildingNumber || "").trim();
  const apartment = String(source.apartment || "").trim();
  const entrance = String(source.entrance || source.entranceNumber || "").trim();
  const floor = String(source.floor || "").trim();
  return {
    fullAddress: [streetName, building, apartment ? `кв. ${apartment}` : ""]
      .filter(Boolean)
      .join(", "),
    streetId: String(source.streetId || ""),
    streetRef: String(source.streetRef || ""),
    streetName,
    building,
    apartment,
    entrance,
    floor,
    postCode: String(source.postCode || source.postalCode || "").trim(),
    note: String(source.note || source.courierNote || "").trim(),
  };
};

export const buildNovaPoshtaDelivery = (formData = {}) => {
  const method = getNovaPoshtaDeliveryMethod(formData.deliveryMethod);
  if (!method) return undefined;
  const current = formData.novaPoshtaDelivery || createEmptyNovaPoshtaDelivery(method);
  const base = {
    apiVersion: NOVA_POST_API_VERSION,
    provider: NOVA_POST_PROVIDER,
    method,
    serviceType: "",
    city: {
      ...settlementFromSelection(current.city),
      name: String(current.city?.name || formData.city || "").trim(),
    },
  };
  if (method === "courier") {
    return {
      ...base,
      courierAddress: buildCourierAddress({
        ...current.courierAddress,
        street: formData.street,
        buildingNumber: formData.buildingNumber,
        apartment: formData.apartmentNumber,
        postalCode: formData.postalCode,
        entranceNumber: formData.entranceNumber,
        floor: formData.floor,
        courierNote: formData.courierNote,
      }),
    };
  }
  return { ...base, division: divisionFromSelection(current.division) };
};

export const validateNovaPoshtaCheckout = (formData = {}) => {
  const method = getNovaPoshtaDeliveryMethod(formData.deliveryMethod);
  if (!method) return {};
  const delivery = buildNovaPoshtaDelivery(formData);
  const errors = {};
  if (!delivery.city.name) errors.city = true;
  if (!delivery.city.id && !delivery.city.ref) errors.novaPostCityId = true;

  if (method === "branch" || method === "parcelLocker") {
    if (!delivery.division.divisionId) errors.branchNumber = true;
    if (!delivery.division.address && !delivery.division.name) {
      errors.branchNumber = true;
    }
    if (
      delivery.city.id &&
      delivery.division.settlementId &&
      delivery.city.id !== delivery.division.settlementId
    ) {
      errors.branchNumber = true;
    }
  }
  if (method === "courier") {
    if (!delivery.courierAddress.streetName) errors.street = true;
    if (!delivery.courierAddress.building) errors.buildingNumber = true;
    if (!delivery.courierAddress.postCode) errors.postalCode = true;
  }
  return errors;
};
